<template>
    <ul v-if="open && items.length" class="absolute z-40 mt-1 max-h-60 w-full overflow-auto rounded border border-gray-200 bg-white py-1 shadow-lg">
        <slot name="item" :items="items" :active-index="activeIndex" :select="select" :highlight="highlight">
            <li
                v-for="(item, index) in items"
                :key="index"
                :class="{ 'bg-gray-100': activeIndex === index }"
            >
                <a role="button" class="block cursor-pointer px-3 py-1.5 text-sm" @click="select(item)">
                    <span v-html="highlight(item)"></span>
                </a>
            </li>
        </slot>
    </ul>
</template>

<script>
export default {
    name: 'BTypeahead',
    props: {
        value: {
            default: null
        },
        data: {
            type: Array,
            default: () => []
        },
        itemKey: {
            type: String,
            default: null
        },
        limit: {
            type: [Number, String],
            default: 10
        },
        target: {
            type: String,
            required: true
        },
        openOnEmpty: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            open: false,
            query: '',
            activeIndex: 0,
            inputEl: null
        }
    },
    computed: {
        items () {
            const q = (this.query || '').toLowerCase()
            let list = this.data || []
            if (q) {
                list = list.filter(item => this.getLabel(item).toLowerCase().includes(q))
            } else if (!this.openOnEmpty) {
                return []
            }
            return list.slice(0, Number(this.limit) || 10)
        }
    },
    watch: {
        value (val) {
            if (this.inputEl && typeof val === 'string') {
                this.inputEl.value = val
                this.query = val
            } else if (this.inputEl && val && typeof val === 'object') {
                this.inputEl.value = this.getLabel(val)
                this.query = this.getLabel(val)
            }
        }
    },
    methods: {
        getLabel (item) {
            if (item == null) return ''
            if (typeof item === 'string') return item
            if (this.itemKey && item[this.itemKey] != null) return String(item[this.itemKey])
            return String(item.name || item.label || item)
        },
        highlight (item) {
            const label = this.getLabel(item)
            const q = this.query
            if (!q) return label
            const idx = label.toLowerCase().indexOf(q.toLowerCase())
            if (idx === -1) return label
            return (
                label.slice(0, idx) +
                '<strong>' + label.slice(idx, idx + q.length) + '</strong>' +
                label.slice(idx + q.length)
            )
        },
        select (item) {
            this.$emit('input', item)
            if (this.inputEl) {
                this.inputEl.value = this.getLabel(item)
            }
            this.query = this.getLabel(item)
            this.open = false
        },
        onInput (e) {
            this.query = e.target.value
            this.$emit('input', e.target.value)
            this.open = true
            this.activeIndex = 0
        },
        onFocus () {
            if (this.openOnEmpty || this.query) {
                this.open = true
            }
        },
        onBlur () {
            setTimeout(() => {
                this.open = false
            }, 150)
        }
    },
    mounted () {
        this.inputEl = document.querySelector(this.target)
        if (!this.inputEl) return
        this.inputEl.addEventListener('input', this.onInput)
        this.inputEl.addEventListener('focus', this.onFocus)
        this.inputEl.addEventListener('blur', this.onBlur)
        if (this.value) {
            this.inputEl.value = typeof this.value === 'string' ? this.value : this.getLabel(this.value)
            this.query = this.inputEl.value
        }
        // ensure parent is relative for dropdown positioning
        if (this.$el.parentElement) {
            this.$el.parentElement.style.position = 'relative'
        }
    },
    beforeUnmount () {
        if (!this.inputEl) return
        this.inputEl.removeEventListener('input', this.onInput)
        this.inputEl.removeEventListener('focus', this.onFocus)
        this.inputEl.removeEventListener('blur', this.onBlur)
    }
}
</script>
